/**
 * Reloads the Nuxt application with preserved state.
 *
 * @param {string} path - The path to reload the application at.
 * @see https://github.com/nuxt/nuxt/issues/23612
 */
const reloadNuxtApp_ = (path: string) => {
  reloadNuxtApp({ persistState: true, path })
}

/**
 * Nuxt plugin to handle chunk loading errors and application manifest updates.
 *
 * This plugin provides a mechanism to:
 * 1. Track the current navigation path
 * 2. Reload the application if a chunk error occurs
 * 3. Reload the application when the app manifest is updated
 *
 * @type {import('nuxt/app').NuxtPlugin}
 * @name nuxt:chunk-reload-immediate-armani
 */
export default defineNuxtPlugin({
  name: 'nuxt:chunk-reload-immediate-armani',
  setup(nuxtApp) {
    // Remember `to.path` when navigating to a new path: A `chunkError` may occur during navigation, we then want to then reload at `to.path`
    let currentlyNavigationTo: null | string = null

    /**
     * Route middleware to track the current navigation path.
     */
    addRouteMiddleware(to => {
      currentlyNavigationTo = to.path
    })

    /**
     * Hook to reload the application when a chunk error occurs.
     * Uses the last known navigation path or current route.
     */
    nuxtApp.hook('app:chunkError', () =>
      reloadNuxtApp_(currentlyNavigationTo ?? nuxtApp._route.path)
    )

    /**
     * Hook to reload the application when the app manifest is updated.
     * Reloads at the current route path.
     */
    nuxtApp.hook('app:manifest:update', () =>
      reloadNuxtApp_(nuxtApp._route.path)
    )
  },
})
